// bluaIQ — client_question client UI v1 — DATA LAYER + PROJECTION GUARD.
// Sealed contract name: `insulation-lane-checkpoint-v1`
// Spec: docs/SPEC_CLIENT_QUESTION_UI_V1.md (committed, Codex-sealed).
//
// NAMING (spec §0): "F4" was a conversation phase label ONLY. It must never
// appear in an identifier here — `PR-F4A…F4H` is the Financial-Trust money
// series already on main. Local identifiers use `clientQuestion`.
//
// PURE DATA — NO React, NO Prisma, NO fetch, NO network. This module owns the
// boundary between the engine's raw evidence hydration and the client card, and
// the single most dangerous rule in the spec (§3): the field
// `attached_evidence_records` means a 15–16-key RAW record on the engine side
// (carries `raw_text`) and a curated 4-key projection on the client side. Same
// name, opposite safety properties. The allowlist below is the boundary where
// the two are allowed to meet, and it is asserted POSITIVELY (exactly 4 keys),
// because FORBIDDEN_FIELDS is negative-only and cannot catch the near-miss where
// a raw record has `raw_text` stripped but `sheet_number`/`source_page`/`r_value`
// still attached (spec §3.0).

import type { BilingualLabel } from '../../shared/websitePayloadContractV2'

/** The client-facing curated evidence projection — spec §2 field 5, §3.1.
 *  EXACTLY these four keys. `sheet`/`page` are nullable (spec §2.-0). */
export interface ClientQuestionEvidence {
  evidence_id: string
  sheet: string | null
  page: number | null
  /** Authored website-side, ≤160 chars (spec §3.1). Never received from the
   *  engine, never OCR — which is exactly why condition 7's long-evidence test
   *  guards OUR curation, not the engine's output. */
  evidence_quote: string
}

/** Spec §2 field 3 — `value` is the stable key, `label` is what renders. */
export interface ClientQuestionOption {
  value: string
  label: BilingualLabel
}

/** The sealed NINE fields — spec §2, sealed by the Codex final ruling. Product
 *  names, deliberately NOT a mirror of engine internals (the product boundary). */
export interface ClientQuestion {
  question_id: string
  question_text: BilingualLabel
  options: ClientQuestionOption[]
  /** ids only (field 4). */
  attached_evidence: string[]
  /** curated projection (field 5) — see the allowlist guard below. */
  attached_evidence_records: ClientQuestionEvidence[]
  /** field 6 — optional, NEVER preselected. */
  suggested_interpretation: BilingualLabel | null
  /** field 7 — the answer enters as a reversible project `user_truth`. */
  answer_slot: string
  /** field 8 — ≤240 chars. The client sees a fixed short phrase, NEVER this
   *  verbatim value; the auditor keeps and verifies the full string. */
  source_user_truth: string
  /** field 9 — the question says which block it releases WITHOUT a separate
   *  line lookup (spec §2.-1). Auditor-channel prose; the client card renders
   *  its own chrome sentence gated on this field's presence, never this raw
   *  string (it can carry engine references such as "(UT-7)"). */
  linked_blocking_reason: string
}

/** The four keys the client evidence projection is allowed to carry — spec §3.
 *  An allowlist, not a blocklist: `keys.length === 4` catches the near-miss that
 *  FORBIDDEN_FIELDS structurally cannot. */
export const CLIENT_EVIDENCE_ALLOWED_KEYS = ['evidence_id', 'sheet', 'page', 'evidence_quote'] as const

/** Authored-quote ceiling — spec §3.1 / §4.2. The ≤160 cap and the mobile
 *  "never horizontal scroll" guard are the same rule seen twice. */
export const MAX_EVIDENCE_QUOTE_CHARS = 160

/** source_user_truth ceiling — spec §2 field 8. */
export const MAX_SOURCE_USER_TRUTH_CHARS = 240

/** Thrown when a client_question or its evidence violates the sealed contract.
 *  The card must never render a violating question — it throws, it does not
 *  silently truncate or drop (spec §7: "rejected, not silently truncated"). */
export class ClientQuestionContractError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ClientQuestionContractError'
  }
}

/** PRIMARY LOCK (spec §3.0). Assert a single evidence record is EXACTLY the four
 *  curated keys and nothing else, then that its quote respects the ≤160 cap.
 *
 *  A raw 15–16-key engine record FAILS here even when it carries NO `raw_text` —
 *  which is the whole point: the near-miss (`sheet_number`/`source_page`/
 *  `r_value`/… sailing through with `raw_text` stripped) is what FORBIDDEN_FIELDS
 *  cannot catch and this allowlist can. */
export function assertClientEvidenceRecord(record: unknown): ClientQuestionEvidence {
  if (record === null || typeof record !== 'object' || Array.isArray(record)) {
    throw new ClientQuestionContractError('evidence record must be an object')
  }
  const rec = record as Record<string, unknown>
  const keys = Object.keys(rec)
  const allowed = CLIENT_EVIDENCE_ALLOWED_KEYS as readonly string[]
  if (keys.length !== allowed.length || !keys.every((k) => allowed.includes(k))) {
    throw new ClientQuestionContractError(
      `evidence record must have EXACTLY the 4 keys {${allowed.join(', ')}}; got {${keys.join(', ')}}`,
    )
  }
  const { evidence_id, sheet, page, evidence_quote } = rec
  if (typeof evidence_id !== 'string' || evidence_id.length === 0) {
    throw new ClientQuestionContractError('evidence_id must be a non-empty string')
  }
  if (sheet !== null && typeof sheet !== 'string') {
    throw new ClientQuestionContractError('sheet must be a string or null')
  }
  if (page !== null && typeof page !== 'number') {
    throw new ClientQuestionContractError('page must be a number or null')
  }
  if (typeof evidence_quote !== 'string') {
    throw new ClientQuestionContractError('evidence_quote must be a string')
  }
  if (evidence_quote.length > MAX_EVIDENCE_QUOTE_CHARS) {
    // Rejected, NOT truncated (spec §7). This is the test that bites: the quote
    // is authored in OUR projection, so this guards our own curation.
    throw new ClientQuestionContractError(
      `evidence_quote exceeds ${MAX_EVIDENCE_QUOTE_CHARS} chars (${evidence_quote.length}) — reject, never truncate`,
    )
  }
  return { evidence_id, sheet, page, evidence_quote }
}

function isBilingual(value: unknown): value is BilingualLabel {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as Record<string, unknown>).en === 'string' &&
    typeof (value as Record<string, unknown>).es === 'string'
  )
}

/** Validate and normalise a raw client_question object from the checkpoint
 *  payload into the sealed nine-field shape the card renders. Throws
 *  ClientQuestionContractError on any violation — the card never renders a
 *  question it could not fully validate.
 *
 *  Every evidence record passes the allowlist guard, so a record still carrying
 *  `raw_text` (or any raw engine key) cannot reach the card: condition 5 is
 *  enforced one layer below the component (spec §3.1). */
export function assertClientQuestion(raw: unknown): ClientQuestion {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new ClientQuestionContractError('client_question must be an object')
  }
  const q = raw as Record<string, unknown>

  if (typeof q.question_id !== 'string' || q.question_id.length === 0) {
    throw new ClientQuestionContractError('question_id must be a non-empty string')
  }
  if (!isBilingual(q.question_text)) {
    throw new ClientQuestionContractError('question_text must be {es, en}')
  }
  if (!Array.isArray(q.options) || q.options.length === 0) {
    throw new ClientQuestionContractError('options must be a non-empty array')
  }
  const options: ClientQuestionOption[] = q.options.map((opt, i) => {
    if (opt === null || typeof opt !== 'object') {
      throw new ClientQuestionContractError(`options[${i}] must be an object`)
    }
    const o = opt as Record<string, unknown>
    if (typeof o.value !== 'string' || o.value.length === 0) {
      throw new ClientQuestionContractError(`options[${i}].value must be a non-empty string`)
    }
    if (!isBilingual(o.label)) {
      throw new ClientQuestionContractError(`options[${i}].label must be {es, en}`)
    }
    return { value: o.value, label: { en: o.label.en, es: o.label.es } }
  })

  if (!Array.isArray(q.attached_evidence) || !q.attached_evidence.every((e) => typeof e === 'string')) {
    throw new ClientQuestionContractError('attached_evidence must be a string[]')
  }
  if (!Array.isArray(q.attached_evidence_records)) {
    throw new ClientQuestionContractError('attached_evidence_records must be an array')
  }
  const attached_evidence_records = q.attached_evidence_records.map(assertClientEvidenceRecord)

  if (q.suggested_interpretation !== null && !isBilingual(q.suggested_interpretation)) {
    throw new ClientQuestionContractError('suggested_interpretation must be {es, en} or null')
  }
  if (typeof q.answer_slot !== 'string' || q.answer_slot.length === 0) {
    throw new ClientQuestionContractError('answer_slot must be a non-empty string')
  }
  if (typeof q.source_user_truth !== 'string') {
    throw new ClientQuestionContractError('source_user_truth must be a string')
  }
  if (q.source_user_truth.length > MAX_SOURCE_USER_TRUTH_CHARS) {
    throw new ClientQuestionContractError(
      `source_user_truth exceeds ${MAX_SOURCE_USER_TRUTH_CHARS} chars (${q.source_user_truth.length})`,
    )
  }
  if (typeof q.linked_blocking_reason !== 'string') {
    throw new ClientQuestionContractError('linked_blocking_reason must be a string')
  }

  return {
    question_id: q.question_id,
    question_text: { en: q.question_text.en, es: q.question_text.es },
    options,
    attached_evidence: q.attached_evidence as string[],
    attached_evidence_records,
    suggested_interpretation: q.suggested_interpretation
      ? { en: (q.suggested_interpretation as BilingualLabel).en, es: (q.suggested_interpretation as BilingualLabel).es }
      : null,
    answer_slot: q.answer_slot,
    source_user_truth: q.source_user_truth,
    linked_blocking_reason: q.linked_blocking_reason,
  }
}

/** Extract the RAW client_questions array off a checkpoint-shaped
 *  `insulation_lane` structure WITHOUT validating — never throws. This reads the
 *  REAL path `insulation_lane.client_questions[]` (there is no `payload_v3`
 *  wrapper on the checkpoint payload).
 *
 *  This is the RENDER path (Codex #467 correction 2): each raw item is handed to
 *  a card that validates it INDIVIDUALLY, so one malformed question renders its
 *  own neutral "unavailable" marker instead of throwing and taking the whole
 *  section down. Returns [] when the structure is absent or not an array — spec
 *  §6: no structure yields the empty state and ZERO rows, NEVER a prose parser. */
export function extractRawClientQuestions(insulationLane: unknown): unknown[] {
  if (insulationLane === null || typeof insulationLane !== 'object') return []
  const cq = (insulationLane as Record<string, unknown>).client_questions
  return Array.isArray(cq) ? cq : []
}

/** The question_ids that resolve to a VALID client_question — the ONLY ids a
 *  line's `client_question_ids` link may count, on the Excel «Preguntas» sheet
 *  AND on the website line. Codex #490 P0: the two surfaces DIVERGED — the web
 *  counted any string question_id while Excel required the whole question to
 *  pass assertClientQuestion, so invalidating sg-q-001's question_text made the
 *  web say "2 questions" while Excel said one; and a null entry THREW at the
 *  web's .question_id read. ONE shared helper now, resilient per item: an
 *  invalid or null question is not a link target and never throws — exactly as
 *  it is not rendered wherever questions render. Returns DISTINCT ids as an
 *  ARRAY (Playwright CT props serialize a Set to {}). */
export function validClientQuestionIds(insulationLane: unknown): string[] {
  const ids = new Set<string>()
  for (const raw of extractRawClientQuestions(insulationLane)) {
    try {
      ids.add(assertClientQuestion(raw).question_id)
    } catch {
      // invalid → not counted, not a link target, never a crash.
    }
  }
  return [...ids]
}

/** Read AND validate every client_question — throws on the first violation.
 *  Used by TESTS and by any caller that wants the whole set validated up front;
 *  the render path uses `extractRawClientQuestions` + per-card validation so a
 *  single bad item cannot break the section. */
export function readClientQuestions(insulationLane: unknown): ClientQuestion[] {
  return extractRawClientQuestions(insulationLane).map(assertClientQuestion)
}
